import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Users, DollarSign, Activity,
  Send, Inbox, LogOut, Menu, X, MessageCircle
} from 'lucide-react';

const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/accounts', label: 'Accounts', icon: Users },
  { path: '/transactions', label: 'Transactions', icon: DollarSign },
  { path: '/activity', label: 'Activity', icon: Activity },
  { path: '/email', label: 'Send Email', icon: Send },
  { path: '/emails', label: 'Inbox', icon: Inbox },
  { path: '/support-chats', label: 'Support Chats', icon: MessageCircle },
];

function Navbar() {
  const { user, logout } = useAuth();
  const { unreadSupportCount, unreadEmailCount } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (!user || user.role !== 'admin') {
    return null;
  }

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Mobile Toggle */}
      <button
        className="mobile-sidebar-toggle"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label="Toggle navigation"
      >
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Mobile Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="sidebar-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMobileOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <nav className={`sidebar ${mobileOpen ? 'mobile-open' : ''}`}>
        {/* Brand */}
        <div className="sidebar-brand">
          <svg width="32" height="32" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="brand-icon-svg">
            <defs>
              <linearGradient id="sidebarGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#7B2FF7"/>
                <stop offset="100%" stopColor="#9F44FF"/>
              </linearGradient>
            </defs>
            <rect x="0" y="0" width="80" height="80" rx="16" fill="url(#sidebarGrad)"/>
            <rect x="18" y="15" width="44" height="55" rx="4" fill="white"/>
            <rect x="18" y="15" width="44" height="12" fill="#EDE7FF"/>
            <rect x="24" y="33" width="32" height="4" rx="2" fill="#C7B6FF"/>
            <rect x="24" y="43" width="28" height="4" rx="2" fill="#C7B6FF"/>
            <rect x="24" y="53" width="20" height="4" rx="2" fill="#C7B6FF"/>
          </svg>
          <span className="sidebar-label brand-text">
            <svg width="120" height="20" viewBox="0 0 120 20" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="brandTextGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#7B2FF7"/>
                  <stop offset="100%" stopColor="#9F44FF"/>
                </linearGradient>
              </defs>
              <text x="0" y="16" fontFamily="Inter, Arial, sans-serif" fontSize="17" fontWeight="700">
                <tspan fill="url(#brandTextGrad)">Resu</tspan>
                <tspan fill="#ffffff">nize</tspan>
              </text>
            </svg>
          </span>
        </div>

        {/* Navigation Links */}
        <div className="sidebar-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const badgeVal =
              item.path === '/support-chats' ? unreadSupportCount :
              item.path === '/emails' ? unreadEmailCount : 0;
            const badge = badgeVal > 0 ? badgeVal : null;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link ${active ? 'active' : ''}`}
                onClick={() => setMobileOpen(false)}
              >
                <span className="sidebar-icon" style={{ position: 'relative' }}>
                  <Icon size={20} strokeWidth={active ? 2.2 : 1.8} />
                  {badge && (
                    <span className="nav-badge">{badge > 99 ? '99+' : badge}</span>
                  )}
                </span>
                <span className="sidebar-label">{item.label}</span>
                {badge && <span className="nav-badge-label">{badge > 99 ? '99+' : badge}</span>}
                {active && (
                  <motion.div
                    className="sidebar-active-indicator"
                    layoutId="activeNav"
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>
            );
          })}
        </div>

        {/* Footer */}
        <div className="sidebar-footer">
          <button
            className="sidebar-link logout-link"
            onClick={handleLogout}
          >
            <span className="sidebar-icon">
              <LogOut size={20} strokeWidth={1.8} />
            </span>
            <span className="sidebar-label">Logout</span>
          </button>
        </div>
      </nav>
    </>
  );
}

export default Navbar;
